import React, { useMemo, useState } from 'react';
import { Text, Link, Button, Table, Select, ConfirmationDialog } from '@heartinternet/hi-ui';
import type { Column } from '@heartinternet/hi-ui';
import type { Allowance, BusinessEmailListPageProps } from './types';
import { AssignMailboxModal, type AssignPayload } from './components/AssignMailboxModal';
import { ChangePasswordModal, type ChangePasswordPayload } from './components/ChangePasswordModal';
import { UpgradeMailboxModal } from './components/UpgradeMailboxModal';
import { ManageMenu } from './components/ManageMenu';
import { isAssigned, productLabel, formatExpiry, deriveDomains } from './utils';
import './BusinessEmailListPage.css';

export type { BusinessEmailListPageProps };

export const BusinessEmailListPage: React.FC<BusinessEmailListPageProps> = ({
  allowances,
  domains,
  unauthenticatedLoginUrl,
  parentCommonName,
  paths,
}) => {
  const heading = parentCommonName
    ? `Your Business Email mailboxes attached to product ${parentCommonName}`
    : 'Your Business Email mailboxes';

  const [selectedRows, setSelectedRows] = useState<Set<string | number>>(new Set());
  const filterDomains = useMemo(() => deriveDomains(allowances), [allowances]);
  const [filter, setFilter] = useState('');
  const [sortKey, setSortKey] = useState<string | undefined>(undefined);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const [assign, setAssign] = useState<{ open: boolean; mailboxId: string }>({
    open: false,
    mailboxId: '',
  });
  const [changePw, setChangePw] = useState<{
    open: boolean;
    mailboxId: string;
    mailLoginId: string;
  }>({
    open: false,
    mailboxId: '',
    mailLoginId: '',
  });
  const [upgrade, setUpgrade] = useState<{
    open: boolean;
    mailboxId: string;
    price: string | number;
  }>({
    open: false,
    mailboxId: '',
    price: '',
  });
  const [unassignOpen, setUnassignOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const visibleAllowances = useMemo(() => {
    if (!filter) return allowances;
    return allowances.filter((a) => isAssigned(a) && a.full_login?.split('@')[1] === filter);
  }, [allowances, filter]);

  const sortedAllowances = useMemo(() => {
    if (!sortKey) return visibleAllowances;
    const dir = sortDirection === 'desc' ? -1 : 1;
    // Unassigned rows have no address; sort them as empty.
    const emailKey = (x: Allowance) => x.full_login ?? '';
    return [...visibleAllowances].sort((a, b) => {
      if (sortKey === 'expiry_ts') return (a.expiry_ts - b.expiry_ts) * dir;
      if (sortKey === 'product_type') {
        return productLabel(a.product_type).localeCompare(productLabel(b.product_type)) * dir;
      }
      return emailKey(a).localeCompare(emailKey(b)) * dir;
    });
  }, [visibleAllowances, sortKey, sortDirection]);

  // Mutations mirror manage.js: POST to the existing endpoint, then reload so the Catalyst
  // controller re-renders with fresh stashed data (there is no JSON list API to refetch).
  const postJsonAndReload = async (url: string, body: unknown) => {
    setSubmitting(true);
    try {
      await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json; charset=utf-8' },
        body: JSON.stringify(body),
      });
    } finally {
      window.location.reload();
    }
  };

  const handleAssign = (payload: AssignPayload) => {
    void postJsonAndReload(paths.assign, payload);
  };
  const handleChangePassword = (payload: ChangePasswordPayload) => {
    void postJsonAndReload(paths.setPassword, payload);
  };
  const handleUnassignConfirm = () => {
    void postJsonAndReload(paths.unassign, { id_list: [...selectedRows] });
  };
  const handleUpgradeConfirm = async () => {
    setSubmitting(true);
    try {
      const res = await fetch(`${paths.upgradeBase}/${upgrade.mailboxId}/upgrade`, {
        method: 'POST',
      });
      if (res.ok) {
        window.location.assign('/manage/basket.cgi');
        return;
      }
      window.location.reload();
    } catch {
      window.location.reload();
    }
  };

  const handleFilterChange = (value: string) => {
    setFilter(value);
    setSelectedRows(new Set());
  };

  const handleSort = (key: string, direction: 'asc' | 'desc') => {
    setSortKey(key);
    setSortDirection(direction);
  };

  const columns: Column<Allowance>[] = useMemo(
    () => [
      {
        key: 'full_login',
        label: 'Email Address',
        sortable: true,
        render: (_v: unknown, row: Allowance) =>
          isAssigned(row) ? (
            <Link href={unauthenticatedLoginUrl} className="hi-ui-be-list-page__email">
              {row.full_login}
            </Link>
          ) : (
            <Text variant="body" inline>
              <span className="hi-ui-be-list-page__unassigned">Unassigned</span>
            </Text>
          ),
      },
      {
        key: 'expiry_ts',
        label: 'Expiry',
        sortable: true,
        render: (_v: unknown, row: Allowance) => (
          <Text variant="body" inline>
            {formatExpiry(row.expiry_ts)}
          </Text>
        ),
      },
      {
        key: 'product_type',
        label: 'Product',
        sortable: true,
        render: (_v: unknown, row: Allowance) => (
          <Text variant="body" inline>
            {productLabel(row.product_type)}
          </Text>
        ),
      },
      {
        key: 'manage',
        label: '',
        render: (_v: unknown, row: Allowance) =>
          isAssigned(row) ? (
            <ManageMenu
              row={row}
              onChangePassword={() =>
                setChangePw({
                  open: true,
                  mailboxId: row.id,
                  mailLoginId: row.mail_login_id ?? '',
                })
              }
              onUpgrade={() =>
                setUpgrade({
                  open: true,
                  mailboxId: row.id,
                  price: row.upgrade_price as string | number,
                })
              }
            />
          ) : (
            <Button
              variant="primary"
              size="small"
              onClick={() => setAssign({ open: true, mailboxId: row.id })}
            >
              Assign
            </Button>
          ),
      },
    ],
    [unauthenticatedLoginUrl]
  );

  return (
    <div className="hi-ui-be-list-page">
      <Text variant="heading-1">{heading}</Text>

      <div className="hi-ui-be-list-page__sublinks">
        <Link href="/cp/businessemail/forwarder/list">Manage Forwarders</Link>
        <Link href="/cp/businessemail/forwarder/catchall/list">Manage Catchall Forwarders</Link>
        <Link href="/cp/businessemail/autoresponders">Manage Autoresponders</Link>
      </div>

      <Text variant="body">
        With Business Email, you can access your email at any time from any device, as long as you
        have an internet connection.
      </Text>
      <Text variant="body">
        With all these different ways to access your mail, we recommend you update your password
        every six months to help keep your email secure.
      </Text>

      <div className="hi-ui-be-list-page__toolbar">
        <Button
          variant="secondary"
          onClick={() => setUnassignOpen(true)}
          disabled={selectedRows.size === 0}
        >
          Unassign selected
        </Button>

        {filterDomains.length > 0 && (
          <div className="hi-ui-be-list-page__filter">
            <Text variant="label" inline>
              Filter by:
            </Text>
            <Select
              name="businessemail-domains"
              value={filter}
              onChange={(e) => handleFilterChange(e.target.value)}
              aria-label="Filter by domain"
            >
              <option value="">All mailboxes</option>
              {filterDomains.map((d) => (
                <option key={d} value={d}>
                  {d}
                </option>
              ))}
            </Select>
          </div>
        )}
      </div>

      <Table<Allowance>
        columns={columns}
        data={sortedAllowances}
        selectable
        selectedRows={selectedRows}
        onSelectionChange={setSelectedRows}
        isRowSelectable={isAssigned}
        getRowKey={(row) => row.id}
        onSort={handleSort}
        sortKey={sortKey}
        sortDirection={sortDirection}
        emptyMessage="You have no Business Email mailboxes."
        gridColumns="auto minmax(180px, 2fr) auto auto auto"
        ariaLabel="Business Email mailboxes"
      />

      <AssignMailboxModal
        isOpen={assign.open}
        mailboxId={assign.mailboxId}
        domains={domains}
        submitting={submitting}
        onClose={() => setAssign({ open: false, mailboxId: '' })}
        onSubmit={handleAssign}
      />

      <ChangePasswordModal
        isOpen={changePw.open}
        mailboxId={changePw.mailboxId}
        mailLoginId={changePw.mailLoginId}
        submitting={submitting}
        onClose={() => setChangePw({ open: false, mailboxId: '', mailLoginId: '' })}
        onSubmit={handleChangePassword}
      />

      <UpgradeMailboxModal
        isOpen={upgrade.open}
        upgradePrice={upgrade.price}
        submitting={submitting}
        onClose={() => setUpgrade({ open: false, mailboxId: '', price: '' })}
        onConfirm={handleUpgradeConfirm}
      />

      <ConfirmationDialog
        isOpen={unassignOpen}
        title="Unassign"
        confirmLabel="Yes"
        cancelLabel="No"
        confirmVariant="warning"
        loading={submitting}
        onConfirm={handleUnassignConfirm}
        onCancel={() => setUnassignOpen(false)}
      >
        Are you sure you want to unassign the selected mailboxes? If there are no mailboxes left for
        a certain domain, all forwarders and autoresponders related to it will be deleted. This
        action is irreversible.
      </ConfirmationDialog>
    </div>
  );
};
